/**
 * Вход в приложение: `/` → `/dashboard` → сюда (AK-898, AK-896; AK-866 В-3,
 * макет v2 экран 3, UC-1/UC-2/UC-7).
 *
 * Какое хозяйство открыть и куда вести — чистое правило
 * `components/scope/resolveEntry.ts` (там же приоритет источников: ссылка →
 * сессия → память последнего выбора → единственное → кластер → «Холдинг» →
 * первое по алфавиту). Этот компонент правило только исполняет:
 *
 *   · «Холдинг» — экран «Холдинг» (`DashboardPage`), в т.ч. когда областей
 *     нет вовсе (компания не заведена — дашборд сам уводит в онбординг) или
 *     список не загрузился (он покажет ошибку с «Повторить»);
 *   · кластер / отделение — `/farm`: попросит выбрать хозяйство в
 *     переключателе (канон `PICK_FARM_TEXT`);
 *   · хозяйство — по заполненности двойника: `GET /farms/{id}/preparation`
 *     (тот же ключ кэша `['farm-card', id, 'preparation']`, что у полосы
 *     AK-900 — второго запроса экран «Данных хозяйства» не сделает):
 *     заполнен → `/map` (хозяйство уже в области), иначе → `/farm/<id>`.
 *
 * Без мерцаний (CLAUDE.md «отсутствие усталости»): один экран «Загружаем…»,
 * пока не известны И область, И заполненность, — и ОДИН `replace`-редирект в
 * конец. Промежуточных адресов в истории не остаётся. Если хозяйство выбрано
 * не сессией (память, первое по алфавиту, ссылка), редирект ждёт, пока
 * область сессии его догонит (`POST /scope/set` → оптимистичный `current`):
 * иначе человек стоит на экране X, а переключатель показывает прежнее.
 * Запись области не удалась → ошибка с «Повторить», редиректа нет: молчащий
 * set — урок AK-790.
 *
 * Ссылка `?farm=` (UC-7): переключает область через `useFarmDeepLink` (один
 * `/scope/set`), после чего `onDeepLinkApplied` убирает `farm` из адреса;
 * хозяйство не из доступных → «Нет доступа к хозяйству», область не трогаем,
 * данных не запрашиваем.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorState } from '@/components/ErrorState';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { readLastScope } from '@/components/scope/lastScope';
import { resolveEntryTarget, startScreenFor } from '@/components/scope/resolveEntry';
import { useAvailableScopes } from '@/components/scope/useAvailableScopes';
import { useFarmDeepLink } from '@/components/scope/useFarmDeepLink';
import { useScopeSetter } from '@/components/scope/useScopeSetter';
import { DashboardPage } from '@/pages/dashboard';
import { useFarmPreparation } from '@/pages/farm/preparation';

/** `validateSearch` входа — только `?farm=` (присланная ссылка). */
export function entryFarmSearch(search: Record<string, unknown>): { farm?: string } {
  return { farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined };
}

export interface DashboardEntryProps {
  /** `?farm=` из адреса — присланная ссылка на хозяйство. */
  farmSearch?: string;
  /** Ссылка отработала — убрать `farm` из адреса (форму search знает маршрут). */
  onDeepLinkApplied?: () => void;
}

const NOOP = () => undefined;

function Loading() {
  return (
    <AppShell>
      <div className="max-w-[1200px] text-muted-foreground">Загружаем…</div>
    </AppShell>
  );
}

export function DashboardEntry({ farmSearch, onDeepLinkApplied }: DashboardEntryProps) {
  const scopes = useAvailableScopes();
  const appliedRef = useRef(onDeepLinkApplied);
  appliedRef.current = onDeepLinkApplied;
  const { link } = useFarmDeepLink(farmSearch, () => (appliedRef.current ?? NOOP)());
  const { setScope, error: setError, clearError } = useScopeSetter('DashboardEntry');

  const raw = scopes.data;
  const data = raw && Array.isArray(raw.available) && raw.current ? raw : undefined;
  // Память читается один раз на вход: пока мы здесь, выбор никто не меняет.
  const last = useMemo(() => readLastScope(), []);

  const target = resolveEntryTarget({
    data,
    scopesFailed: !!raw || scopes.isError,
    link,
    last,
  });

  const farmId = target.kind === 'farm' ? target.farmId : null;
  // Один запрос заполненности, общий ключ с полосой двойника (AK-900).
  const prep = useFarmPreparation(farmId);
  const prepSettled = prep.isSuccess || prep.isError;

  const sessionMatches =
    !!farmId && data?.current.scope_type === 'farm' && data.current.scope_id === farmId;
  const needWrite = target.kind === 'farm' && target.write && !sessionMatches;

  // Запись области — один раз на хозяйство (и ещё раз по явному «Повторить»).
  const [attempt, setAttempt] = useState(0);
  const writtenFor = useRef<string | null>(null);
  useEffect(() => {
    if (!needWrite || !farmId) return;
    const key = `${farmId}#${attempt}`;
    if (writtenFor.current === key) return;
    writtenFor.current = key;
    void setScope({ scope_type: 'farm', scope_id: farmId });
  }, [needWrite, farmId, attempt, setScope]);

  const retryWrite = useCallback(() => {
    clearError();
    setAttempt((a) => a + 1);
  }, [clearError]);

  switch (target.kind) {
    case 'loading':
      return <Loading />;
    case 'holding':
      return <DashboardPage />;
    case 'pick':
      return <Navigate to="/farm" replace />;
    case 'no-access':
      return (
        <AppShell>
          <div className="max-w-[1200px]">
            <FarmScopeNotice scope={{ status: 'no-access', message: null, retry: NOOP }} />
          </div>
        </AppShell>
      );
    case 'link-error':
      return (
        <AppShell>
          <div className="max-w-[1200px]">
            <FarmScopeNotice
              scope={{ status: 'error', message: target.message, retry: target.retry }}
            />
          </div>
        </AppShell>
      );
    case 'farm':
      break;
  }

  // Единственное хозяйство открываем сразу: запись области — best-effort
  // (у роли хозяйства она уже совпадает, сеть не трогается). Остальные
  // источники ждут, пока сессия догонит выбор.
  const awaitSession = target.source !== 'only' && target.source !== 'session';
  if (awaitSession && !sessionMatches) {
    if (target.write && setError) {
      return (
        <AppShell>
          <ErrorState
            message={`Не удалось открыть хозяйство: ${setError}`}
            onRetry={retryWrite}
          />
        </AppShell>
      );
    }
    return <Loading />;
  }
  if (!prepSettled) return <Loading />;

  if (startScreenFor(prep.data) === 'map') return <Navigate to="/map" replace />;
  return <Navigate to="/farm/$farmId" params={{ farmId: target.farmId }} replace />;
}

export default DashboardEntry;
